// Copyright (c) StrangeBee 2024

import React, { ReactElement, useState, useEffect } from 'react';
import { Input } from 'antd';
import { ICONS } from '@Constants/iconsApp';
import ConfirmButtons from '@Atoms/Buttons/ConfirmButtons/ConfirmButtons';

import './EditableText.scss';

interface EditableTextProps {
    readOnly?: boolean;
    text?: string;
    saveValue?: (value: string) => void;
    allowEmpty?: boolean;
    customComponent?: ReactElement;
}

const EditableText = ({
    readOnly = false,
    text = '',
    saveValue,
    allowEmpty = true,
    customComponent,
}: EditableTextProps): ReactElement => {
    const [value, setValue] = useState<string>(text);
    const [inEdit, setInEdit] = useState<boolean>(false);
    useEffect(() => {
        setValue(text);
    }, [text]);

    const isReadOnly = readOnly || !saveValue;

    const cancelForm = (): void => {
        setValue(text);
        setInEdit(false);
    };

    const canSave = allowEmpty || value;

    const validForm = (): void => {
        if (canSave) {
            saveValue?.(value);
            setInEdit(false);
        }
    };

    if (!inEdit) {
        return (
            <div
                className={`editable-text form-close ${isReadOnly ? 'readOnly' : ''}`}
                onClick={() => !isReadOnly && setInEdit(true)}
            >
                {customComponent || (
                    <>
                        <span className="text-value">{value}</span>
                        <ICONS.EditIcon />
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="editable-text">
            <Input value={value} onChange={(e): void => setValue(e.target.value)} />
            <ConfirmButtons onCancel={cancelForm} onConfirm={validForm} disabled={!canSave} />
        </div>
    );
};

export default EditableText;
